/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Dimensions, Platform, View } from 'react-native';

import ContentCardView from '@airasia-phoenix/react-native/widgets/ContentCardView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useDeviceInfo } from '@airasia-rn/ui';
import styles from './RecommendationCard.styles';

import { useLogger } from '@airasia-rn/core-logger';
import mapRecommendationToCardProps from '../../utils/mapRecommendationToCardProps';
import {
  RecommendationEvents,
  trackRecommendationClick,
} from '../../analytics/recommendation';

const fallbackIconByLob: Record<string, any> = {
  hotel: pd.icon.main.maps.hotel,
  flight: pd.icon.main.maps.flight,
  food: pd.icon.main.maps.fastfood,
  ride: pd.icon.main.maps.directions_car,
  shop: pd.icon.main.actions.shopping_bag,
  promotions: pd.icon.main.maps.terrain,
  ancillary: pd.icon.main.maps.terrain,
  community: pd.icon.main.maps.terrain,
  rewards: pd.icon.main.maps.terrain,
  ad_v3: pd.icon.main.maps.terrain,
  generic: pd.icon.main.maps.terrain,
};

const WEB_CARD_IMAGE_HEIGHT = 236;
const SIDE_PADDING = 16;
const COLUMN_GAP = 12;
const NUM_COLUMNS = 2;

const getMobileCardSize = () => {
  const { width: screenWidth } = Dimensions.get('window');
  return Math.floor(
    (screenWidth - SIDE_PADDING * 2 - COLUMN_GAP * (NUM_COLUMNS - 1)) /
      NUM_COLUMNS,
  );
};

const RecommendationCard = ({
  item,
  trackClicks = false,
  onTrackClick,
  onNavigate,
  onPressCta,
  imageRefreshKey,
}: {
  item: any;
  trackClicks?: boolean;
  onTrackClick?: (item: any) => void;
  onNavigate?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void;
  onPressCta?: (params: { deepLink?: string }) => void;
  imageRefreshKey?: number | string;
}) => {
  const { isMobileWidth } = useDeviceInfo();
  const { logEvent } = useLogger();
  const [mounted, setMounted] = useState(Platform.OS !== 'web');
  useEffect(() => setMounted(true), []);
  const cardSize = useMemo(
    () => (isMobileWidth ? (mounted ? getMobileCardSize() : 0) : undefined),
    [mounted, isMobileWidth],
  );
  const baseProps = useMemo(() => {
    return mapRecommendationToCardProps(item, onNavigate, cardSize, onPressCta);
  }, [item, onNavigate, cardSize, onPressCta]);

  const [liveProps, setLiveProps] = useState<typeof baseProps | undefined>(
    undefined,
  );
  const [countdownText, setCountdownText] = useState<string>();
  const effectiveProps = liveProps === undefined ? baseProps : liveProps;

  const image = item?.image?.trim();
  const hasImage = Boolean(image);
  const [isImageLoading, setIsImageLoading] = useState(hasImage);
  const isImageLoadingRef = useRef(hasImage);
  const [imageRetryKey, setImageRetryKey] = useState(0);
  const previousImageRefreshKeyRef = useRef(imageRefreshKey);

  useEffect(() => {
    if (Platform.OS === 'web') {
      isImageLoadingRef.current = hasImage;
      setIsImageLoading(hasImage);
    }
  }, [hasImage, item]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      isImageLoadingRef.current = hasImage;
      setIsImageLoading(hasImage);
    }
  }, [hasImage, image]);

  const setImageLoading = useCallback((isLoading: boolean) => {
    isImageLoadingRef.current = isLoading;
    setIsImageLoading(isLoading);
  }, []);

  const retryImage = useCallback(() => {
    if (!hasImage) return;
    setImageLoading(true);
    setImageRetryKey((current) => current + 1);
  }, [hasImage, setImageLoading]);

  const handleImageLoad = useCallback(
    () => setImageLoading(false),
    [setImageLoading],
  );
  const handleImageError = useCallback(
    () => setImageLoading(true),
    [setImageLoading],
  );

  useEffect(() => {
    if (Platform.OS === 'web') return;

    if (
      previousImageRefreshKeyRef.current !== imageRefreshKey &&
      isImageLoadingRef.current
    ) {
      retryImage();
    }
    previousImageRefreshKeyRef.current = imageRefreshKey;
  }, [imageRefreshKey, retryImage]);

  useEffect(() => {
    const timer = (effectiveProps as any)?.__timer;

    if (!timer) {
      setCountdownText(undefined);
      return;
    }

    const updateTimer = () => {
      const diff = new Date(timer.timerDate).getTime() - Date.now();

      if (diff <= 0) {
        setCountdownText(undefined);
        const refreshed = mapRecommendationToCardProps(
          item,
          onNavigate,
          isMobileWidth ? getMobileCardSize() : undefined,
          onPressCta,
        );

        setLiveProps(refreshed);

        return;
      }

      const hours = Math.floor(diff / 3600000);

      const minutes = Math.floor((diff % 3600000) / 60000);

      const seconds = Math.floor((diff % 60000) / 1000);

      setCountdownText(`${timer.prefix} ${hours}h ${minutes}m ${seconds}s`);
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [effectiveProps, item, isMobileWidth]);

  const finalProps = useMemo(() => {
    const props = liveProps === undefined ? baseProps : liveProps;

    if (!props) return null;

    if (countdownText && props.supportingInfo) {
      return {
        ...props,

        supportingInfo: {
          ...props.supportingInfo,

          supportingText: countdownText,
        },
      };
    }

    return props;
  }, [baseProps, liveProps, countdownText]);

  const propsWithLoadCallback = useMemo(() => {
    if (!finalProps || !hasImage) return finalProps;
    return {
      ...finalProps,
      image: {
        ...(finalProps.image as object),
        onLoad: handleImageLoad,
        ...(Platform.OS === 'web' ? {} : { onError: handleImageError }),
      },
    };
  }, [finalProps, hasImage, handleImageError, handleImageLoad]);

  if (!propsWithLoadCallback) return null;

  const handlePress = () => {
    if (trackClicks) {
      logEvent(RecommendationEvents.Click, trackRecommendationClick(item));
    }
    onTrackClick?.(item);
    propsWithLoadCallback.onPress?.();
  };

  const lobKey = String(item?.lob ?? '').toLowerCase();
  const fallbackIcon = fallbackIconByLob[lobKey] ?? fallbackIconByLob.generic;
  const resolvedImageHeight = (propsWithLoadCallback as any)?.imageHeight;
  const imageAreaHeight =
    typeof resolvedImageHeight === 'number'
      ? resolvedImageHeight
      : isMobileWidth
        ? cardSize
        : WEB_CARD_IMAGE_HEIGHT;

  const showPlaceholder = !hasImage || isImageLoading;

  return (
    <View style={styles.cardContainer}>
      <ContentCardView
        key={Platform.OS === 'web' ? undefined : `${image}:${imageRetryKey}`}
        {...propsWithLoadCallback}
        onPress={handlePress}
      />
      {showPlaceholder && (
        <View
          pointerEvents="none"
          style={[
            styles.fallbackOverlay,
            {
              height:
                imageAreaHeight != null
                  ? imageAreaHeight + pd.ref.space.spacing10.px
                  : imageAreaHeight,
              backgroundColor: getColor(pd.sys.colour.container_low),
            },
          ]}
        >
          <IconView icon={fallbackIcon} width={24} height={24} />
        </View>
      )}
    </View>
  );
};

export default React.memo(RecommendationCard);
